import { useEffect, useRef } from 'react';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Accesibilidad de diálogos (VLT-299 · pase de teclado): Escape cierra,
 *  el foco entra al abrir y VUELVE al disparador al cerrar y, en modales
 *  (trap=true), Tab circula dentro sin escaparse a la página de fondo.
 *  Colgar el ref del contenedor del diálogo (el panel, no el overlay). */
export function useDialogA11y<T extends HTMLElement = HTMLDivElement>(
  open: boolean,
  onClose: () => void,
  { trap = true } = {},
) {
  const ref = useRef<T | null>(null);
  const returnTo = useRef<HTMLElement | null>(null);
  // onClose vive en un ref: los componentes lo pasan inline y no debe
  // re-disparar el efecto (perdería el elemento al que devolver el foco).
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    returnTo.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    // Foco inicial: primer elemento interactivo del diálogo (tras el mount animado).
    const focusFirst = () => {
      const node = ref.current;
      if (!node || node.contains(document.activeElement)) return;
      node.querySelector<HTMLElement>(FOCUSABLE)?.focus();
    };
    const raf = requestAnimationFrame(focusFirst);

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        closeRef.current();
        return;
      }
      if (!trap || e.key !== 'Tab') return;
      const node = ref.current;
      if (!node) return;
      const items = Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (!node.contains(active)) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('keydown', onKey, true);
      returnTo.current?.focus();
    };
  }, [open, trap]);

  return ref;
}
